import { useEffect, useState } from 'react'
import { DollarSign, Cpu, BarChart2, Loader2, AlertCircle } from 'lucide-react'
import { engineApiClient } from '@/lib/apiClient'
import type { CostsResponse } from '@/lib/contracts'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatUsd(value: number): string {
  return `$${value.toFixed(4)}`
}

function formatUsdShort(value: number): string {
  return `$${value.toFixed(2)}`
}

function formatTokens(value: number): string {
  return value.toLocaleString()
}

function formatUsageType(raw: string): string {
  return raw
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

// ─── Stat tile ────────────────────────────────────────────────────────────────

interface StatTileProps {
  icon: React.ReactNode
  label: string
  value: string
}

function StatTile({ icon, label, value }: StatTileProps) {
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-white/10 bg-[#233D4C] p-5">
      <div className="flex items-center gap-2 text-muted-foreground">
        {icon}
        <span className="text-sm font-medium">{label}</span>
      </div>
      <p className="text-2xl font-bold text-white tracking-tight">{value}</p>
    </div>
  )
}

// ─── Simple table ─────────────────────────────────────────────────────────────

interface Column<T> {
  label: string
  render: (row: T) => React.ReactNode
  className?: string
}

function DataTable<T>({ columns, rows, emptyLabel }: {
  columns: Column<T>[]
  rows: T[]
  emptyLabel?: string
}) {
  if (rows.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-muted-foreground">
        {emptyLabel ?? 'No data yet'}
      </p>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-white/10">
            {columns.map((col) => (
              <th
                key={col.label}
                className={`pb-2 pt-1 text-left text-xs font-medium text-muted-foreground ${col.className ?? ''}`}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={i}
              className="border-b border-white/5 last:border-0 hover:bg-white/5 transition-colors"
            >
              {columns.map((col) => (
                <td key={col.label} className={`py-2.5 text-foreground ${col.className ?? ''}`}>
                  {col.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ─── Section card ─────────────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-white/10 bg-[#233D4C] p-5 space-y-4">
      <h2 className="text-sm font-semibold text-white/80 uppercase tracking-wider">{title}</h2>
      {children}
    </div>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function CostDashboard() {
  const [data, setData] = useState<CostsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    engineApiClient
      .getCosts()
      .then(setData)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Failed to load costs'))
      .finally(() => setLoading(false))
  }, [])

  const avgCostPerProject =
    data && data.byProject.length > 0
      ? data.totalCostUsd / data.byProject.length
      : 0

  if (loading) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-3">
        <Loader2 className="h-6 w-6 animate-spin text-[#FD802E]" />
        <p className="text-sm text-gray-400">Loading cost data…</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <div className="w-12 h-12 bg-red-500/10 rounded-full flex items-center justify-center">
          <AlertCircle className="h-6 w-6 text-red-400" />
        </div>
        <p className="text-sm text-red-400 font-medium">Unable to load cost data. Try refreshing.</p>
        <button
          onClick={() => window.location.reload()}
          className="text-xs text-gray-300 hover:text-white border border-white/10 px-4 py-2 rounded-lg hover:border-[#FD802E]/40 transition-colors"
        >
          Retry
        </button>
      </div>
    )
  }

  const isEmpty = !data || data.totalTokens === 0

  return (
    <div className="min-h-screen bg-[#02182B] px-6 py-8 space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Cost Tracking</h1>
        <p className="mt-1 text-sm text-muted-foreground">LLM usage and spend across all projects</p>
      </div>

      {isEmpty ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-white/10 bg-[#233D4C] py-20 gap-3">
          <BarChart2 className="h-10 w-10 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">No AI usage recorded yet.</p>
          <p className="text-xs text-muted-foreground/60">
            Run a search to start tracking costs.
          </p>
        </div>
      ) : (
        <>
          {/* Stat tiles */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <StatTile
              icon={<DollarSign className="h-4 w-4" />}
              label="Total Spend (all time)"
              value={formatUsdShort(data!.totalCostUsd)}
            />
            <StatTile
              icon={<Cpu className="h-4 w-4" />}
              label="Total Tokens Used"
              value={formatTokens(data!.totalTokens)}
            />
            <StatTile
              icon={<BarChart2 className="h-4 w-4" />}
              label="Avg Cost per Project"
              value={formatUsdShort(avgCostPerProject)}
            />
          </div>

          {/* By Model */}
          <Section title="By Model">
            <DataTable
              rows={data!.byModel}
              emptyLabel="No model data yet"
              columns={[
                {
                  label: 'Model',
                  render: (r) => <span className="font-mono text-xs text-[#FD802E]">{r.model}</span>,
                },
                {
                  label: 'Calls',
                  render: (r) => r.calls.toLocaleString(),
                  className: 'text-right',
                },
                {
                  label: 'Tokens',
                  render: (r) => formatTokens(r.totalTokens),
                  className: 'text-right',
                },
                {
                  label: 'Cost',
                  render: (r) => (
                    <span className="text-green-400 font-medium">{formatUsd(r.totalCostUsd)}</span>
                  ),
                  className: 'text-right',
                },
              ]}
            />
          </Section>

          {/* By Usage Type */}
          <Section title="By Usage Type">
            <DataTable
              rows={data!.byUsageType}
              emptyLabel="No usage type data yet"
              columns={[
                {
                  label: 'Type',
                  render: (r) => (
                    <span className="text-white/90">{formatUsageType(r.usageType)}</span>
                  ),
                },
                {
                  label: 'Calls',
                  render: (r) => r.calls.toLocaleString(),
                  className: 'text-right',
                },
                {
                  label: 'Tokens',
                  render: (r) => formatTokens(r.totalTokens),
                  className: 'text-right',
                },
                {
                  label: 'Cost',
                  render: (r) => (
                    <span className="text-green-400 font-medium">{formatUsd(r.totalCostUsd)}</span>
                  ),
                  className: 'text-right',
                },
              ]}
            />
          </Section>

          {/* By Project */}
          <Section title="By Project">
            <DataTable
              rows={data!.byProject}
              emptyLabel="No project data yet"
              columns={[
                {
                  label: 'Project',
                  render: (r) => (
                    <span className="text-white/90">{r.projectTitle}</span>
                  ),
                },
                {
                  label: 'Calls',
                  render: (r) => r.calls.toLocaleString(),
                  className: 'text-right',
                },
                {
                  label: 'Tokens',
                  render: (r) => formatTokens(r.totalTokens),
                  className: 'text-right',
                },
                {
                  label: 'Cost',
                  render: (r) => (
                    <span className="text-green-400 font-medium">{formatUsd(r.totalCostUsd)}</span>
                  ),
                  className: 'text-right',
                },
              ]}
            />
          </Section>
        </>
      )}
    </div>
  )
}
